import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import DashboardShell from "../components/layout/DashboardShell";
import api, { formatApiError, API_BASE } from "../lib/api";
import { formatDateTime } from "../lib/datetime";
import { Button, Pill } from "../components/common/Primitives";
import { FileText, Upload, MessageSquare, Download } from "lucide-react";

/**
 * Borrower-facing view of their own debt cases. Read-only on details
 * (admin owns pipeline transitions); can upload documents + add notes.
 */
export default function MyDebtCases() {
  const { id } = useParams();
  return id ? <CaseDetail caseId={id} /> : <CasesList />;
}

function CasesList() {
  const [rows, setRows] = useState([]);
  const [stageCounts, setStageCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    api.get("/debt/cases").then((r) => {
      setRows(r.data.items || []); setStageCounts(r.data.stage_counts || {});
    }).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <DashboardShell>
      <Toaster richColors position="top-right" />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="overline">DEBT MARKETPLACE</div>
            <h1 className="font-display text-3xl mt-1">My Debt Cases</h1>
          </div>
          <Link to="/money" className="text-xs font-heading bg-gold text-navy px-3 py-2 rounded" data-testid="new-debt-case-btn">
            + New Debt Case
          </Link>
        </div>
        {loading ? <div className="text-sm text-ink-muted italic">Loading…</div> :
          rows.length === 0 ? (
            <div className="text-sm text-ink-muted italic border border-navy/10 rounded p-6 text-center" data-testid="debt-cases-empty">
              No debt cases yet. <Link to="/money" className="text-navy underline">Raise your first request</Link>.
            </div>
          ) : (
            <div className="border border-navy/10 rounded divide-y divide-navy/[0.06]">
              {rows.map((c) => (
                <Link to={`/my-debt-cases/${c.case_id}`} key={c.case_id} data-testid={`debt-case-${c.case_id}`}
                  className="p-3 flex items-center justify-between hover:bg-navy/[0.02]">
                  <div className="min-w-0 flex-1">
                    <div className="font-heading text-ink">{c.company_name}</div>
                    <div className="text-[10px] font-mono text-ink-muted mt-0.5">
                      ₹{Number(c.amount_inr || 0).toLocaleString("en-IN")} · {c.purpose} · {c.tenure_months}m · {formatDateTime(c.created_at)}
                    </div>
                  </div>
                  <Pill tone="gold">{c.stage}</Pill>
                </Link>
              ))}
            </div>
          )
        }
      </div>
    </DashboardShell>
  );
}

function CaseDetail({ caseId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("pnl");
  const [file, setFile] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/debt/cases/${caseId}`).then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (note.trim().length < 2) return;
    try {
      await api.post(`/debt/cases/${caseId}/notes`, { body: note.trim() });
      setNote(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const upload = async () => {
    if (!file) { toast.error("Choose a file first."); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("doc_type", docType); fd.append("file", file);
      await api.post(`/debt/cases/${caseId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded"); setFile(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  if (loading || !data) return <DashboardShell><div className="p-6 text-sm text-ink-muted italic">Loading…</div></DashboardShell>;
  const c = data.case;
  return (
    <DashboardShell>
      <Toaster richColors position="top-right" />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-8 space-y-6" data-testid={`debt-case-detail-${caseId}`}>
        <div>
          <div className="overline">DEBT CASE · {c.case_id}</div>
          <h1 className="font-display text-3xl mt-1">{c.company_name}</h1>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Pill tone="gold">{c.stage}</Pill>
            <span className="text-[10px] font-mono text-ink-muted">₹{Number(c.amount_inr).toLocaleString("en-IN")} · {c.purpose} · {c.tenure_months} months · {c.security_type}</span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="border border-navy/10 rounded p-4 bg-white/60">
          <div className="overline mb-3">Pipeline</div>
          <div className="flex items-center gap-1 overflow-x-auto pb-2">
            {(data.pipeline || []).map((s) => {
              const active = s === c.stage;
              const passed = (data.pipeline.indexOf(c.stage) >= data.pipeline.indexOf(s));
              return (
                <div key={s} className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded shrink-0 ${active ? "bg-gold/20 text-[#8A6600]" : passed ? "bg-emerald-500/10 text-emerald-800" : "bg-navy/5 text-ink-muted"}`}>
                  {s}
                </div>
              );
            })}
          </div>
        </div>

        {/* Documents */}
        <div className="border border-navy/10 rounded p-4 bg-white/60">
          <div className="overline mb-3">Documents</div>
          {(data.documents || []).length === 0 ? (
            <div className="text-xs text-ink-muted italic mb-3">No documents uploaded yet.</div>
          ) : (
            <div className="divide-y divide-navy/[0.06] mb-3">
              {data.documents.map((d) => (
                <div key={d.doc_id} className="py-2 flex items-center justify-between" data-testid={`debt-doc-${d.doc_id}`}>
                  <div>
                    <div className="text-sm font-heading text-ink">{d.label || d.filename}</div>
                    <div className="text-[10px] font-mono text-ink-muted">{d.doc_type} · {formatDateTime(d.uploaded_at)}</div>
                  </div>
                  <a href={`${API_BASE}/debt/cases/${caseId}/documents/${d.doc_id}`} target="_blank" rel="noopener noreferrer"
                    className="text-xs text-navy hover:underline inline-flex items-center gap-1" data-testid={`debt-doc-dl-${d.doc_id}`}>
                    <Download size={12} /> Download
                  </a>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Doc type</label>
              <select value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="debt-doc-type"
                className="mt-0.5 px-2 py-1.5 border border-navy/15 rounded text-xs bg-white">
                {["pnl","balance_sheet","banking_12m","gst_returns","itr","audit_report","sanction_letter","existing_loan_statement","cma","moa_aoa","collateral_valuation","other"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <input type="file" onChange={(e) => setFile(e.target.files?.[0])} className="text-xs" data-testid="debt-doc-file" />
            <Button onClick={upload} disabled={uploading || !file} testId="debt-doc-upload">
              <Upload size={12} className="inline mr-1" />{uploading ? "Uploading…" : "Upload"}
            </Button>
          </div>
        </div>

        {/* Notes */}
        <div className="border border-navy/10 rounded p-4 bg-white/60">
          <div className="overline mb-3">Notes & communication</div>
          <div className="space-y-2 mb-3">
            {(data.notes || []).map((n) => (
              <div key={n.note_id} className="border border-navy/10 rounded p-2 text-xs" data-testid={`debt-note-${n.note_id}`}>
                <div className="text-[10px] font-mono text-ink-muted mb-1">
                  {n.author_email} · {n.author_role} · {formatDateTime(n.at)}
                </div>
                <div className="text-ink">{n.body}</div>
              </div>
            ))}
            {(data.notes || []).length === 0 && <div className="text-xs text-ink-muted italic">No messages yet.</div>}
          </div>
          <div className="flex gap-2">
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ask a question or provide additional context…"
              className="flex-1 px-3 py-2 border border-navy/15 rounded text-sm" data-testid="debt-note-input" />
            <Button onClick={addNote} testId="debt-note-submit"><MessageSquare size={12} className="inline mr-1" />Post</Button>
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}
